/**
 * Supplier-scoped product picker for the purchase-order line grid.
 *
 * Why it is not the quotation/PI picker:
 *  - A PO line MUST belong to the PO's supplier. The server re-checks every line
 *    against `products.supplier_id` in poService.assertLinesMatchSupplier() and
 *    throws 400 `supplier_mismatch`, so this filter is convenience only — never
 *    the security boundary.
 *  - The filter has to run on the SERVER. 1118 of 1332 products carry a
 *    supplier_id and 1103 of those belong to a single supplier, so narrowing a
 *    <=200-row page in the browser would silently hide most of the catalog.
 *    `useProducts({ supplierId })` maps to the `supplierId` query param.
 *  - It shows COST, not the selling price: a PO buys at cost, and the whole
 *    purchase-order module already sits behind `supplier_cost:view`.
 *
 * The picker only reports the picked product; turning it into a line draft is
 * PoLinesEditor's job (poLineFromProduct), which keeps this file free of the
 * line-draft shape.
 */
import { useState } from "react";
import { Plus, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ProductSummaryDto } from "@hisense/shared";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useProducts } from "@/api/hooks";
import { formatQty, formatUnitPrice } from "@/domain/money";

const PAGE_SIZE = 25;

/** One grid template for the header and every row, so the columns stay aligned. */
const COLS =
  "grid grid-cols-[minmax(5rem,8rem)_minmax(0,1fr)_minmax(0,9rem)_4.5rem_4.5rem_7rem] items-center gap-2 text-xs";

export interface PoProductPickerProps {
  /** The PO header's supplier. `null` disables the whole picker. */
  supplierId: string | null;
  /** Shown as the search scope so it is obvious the catalog is narrowed. */
  supplierName: string | null;
  onPick: (p: ProductSummaryDto) => void;
  /**
   * Product ids already on the grid — rendered disabled rather than hidden, so
   * the row still explains why it cannot be picked again.
   */
  disabledIds?: ReadonlySet<string>;
}

export function PoProductPicker({
  supplierId,
  supplierName,
  onPick,
  disabledIds,
}: PoProductPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // No supplier yet: the catalog cannot be scoped, so adding a product could
  // only produce lines the server would reject. Say so instead of failing late.
  if (!supplierId) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" disabled>
          <Plus className="size-3.5" /> {t("purchaseOrders.addProduct")}
        </Button>
        <span className="text-xs text-ink-faint">
          {t("purchaseOrders.selectSupplierFirst")}
        </span>
      </div>
    );
  }

  if (!open) {
    return (
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <Plus className="size-3.5" /> {t("purchaseOrders.addProduct")}
      </Button>
    );
  }

  // Mounted only while open AND scoped, so the query never fires unfiltered.
  return (
    <PoProductSearch
      supplierId={supplierId}
      supplierName={supplierName}
      onPick={onPick}
      disabledIds={disabledIds}
      onClose={() => setOpen(false)}
    />
  );
}

function PoProductSearch({
  supplierId,
  supplierName,
  onPick,
  disabledIds,
  onClose,
}: PoProductPickerProps & { supplierId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const results = useProducts({
    q: q.trim() || undefined,
    supplierId,
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const rows = results.data?.data ?? [];
  const total = results.data?.total ?? rows.length;

  return (
    <div
      className="w-full max-w-2xl"
      onKeyDown={(e) => {
        // Escape closes the picker only; it must not reach the editor form.
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          className="h-8 flex-1 text-xs"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("products.searchPlaceholder")}
          leading={<Search />}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="text-ink-faint hover:text-ink"
        >
          <X className="size-4" />
        </button>
      </div>

      {supplierName ? (
        <p className="mt-1 text-2xs text-ink-faint">
          {t("common.supplier")} · <span className="text-ink-soft">{supplierName}</span>
        </p>
      ) : null}

      <div
        className={`mt-1 max-h-64 overflow-y-auto rounded-xs border border-line bg-surface transition-opacity ${
          results.isFetching && !results.isLoading ? "opacity-60" : ""
        }`}
        aria-busy={results.isFetching || undefined}
      >
        {results.isError ? (
          <div className="flex items-center justify-between gap-2 px-2 py-2">
            <span className="text-xs text-danger">{t("products.errorLoad")}</span>
            <Button variant="secondary" size="sm" onClick={() => void results.refetch()}>
              {t("common.retry")}
            </Button>
          </div>
        ) : results.isLoading ? (
          <p className="px-2 py-2 text-xs text-ink-faint">{t("common.loading")}</p>
        ) : rows.length === 0 ? (
          <p className="px-2 py-2 text-xs text-ink-faint">{t("common.noMatches")}</p>
        ) : (
          <>
            {/* Header and rows share one grid template so the columns line up. */}
            <div
              className={`${COLS} sticky top-0 border-b border-line bg-sunken/90 px-2 py-1 text-2xs font-semibold uppercase tracking-wide text-ink-faint`}
            >
              <span>{t("common.code")}</span>
              <span>{t("common.description")}</span>
              <span>{t("common.colorSize")}</span>
              <span className="text-right">{t("common.moq")}</span>
              {/* Tồn kho: người mua quyết định đặt bao nhiêu dựa vào còn bao
                  nhiêu; dữ liệu đã có sẵn trong DTO. */}
              <span className="text-right">{t("products.stock")}</span>
              <span className="text-right">{t("products.costPrice")}</span>
            </div>
            <ul>
              {rows.map((p) => {
                // Cho chọn LẠI cùng một sản phẩm: mô hình LOT cần hai dòng
                // cùng mã nhưng khác lô/khác ngày giao là chuyện bình thường.
                // Chỉ đánh dấu là "đã có trên đơn", không khoá.
                const already = disabledIds?.has(p.id) ?? false;
                return (
                  <li key={p.id} className="border-b border-line last:border-0">
                    <button
                      type="button"
                      title={already ? t("purchaseOrders.alreadyOnOrder") : undefined}
                      onClick={() => onPick(p)}
                      className={`${COLS} w-full px-2 py-1.5 text-left hover:bg-brand-tint/40 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent`}
                    >
                      <span className="truncate font-mono tabular text-ink">
                        {p.productCode}
                        {p.subRef && <span className="block font-mono text-ink-soft">{p.subRef}</span>}
                        {already ? (
                          <span className="ml-1 text-2xs font-normal text-warn">
                            {t("purchaseOrders.alreadyOnOrderShort")}
                          </span>
                        ) : null}
                      </span>
                      <span className="truncate text-ink-soft">{p.description ?? "—"}</span>
                      <span className="truncate text-ink-faint">
                        {[p.color, p.size].filter(Boolean).join(" · ") || "—"}
                      </span>
                      <span className="text-right font-mono tabular text-ink-soft">
                        {p.moq === null ? "—" : formatQty(p.moq)}
                      </span>
                      <span
                        className={
                          "text-right font-mono tabular " +
                          (p.stockQuantity > 0 ? "text-ink-soft" : "text-ink-faint")
                        }
                      >
                        {formatQty(p.stockQuantity)}
                      </span>
                      <span className="text-right font-mono tabular text-ink">
                        {p.costUnitPrice === null
                          ? "—"
                          : formatUnitPrice(p.costUnitPrice, p.currency)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* Only the first page is fetched: say so rather than imply the list is complete. */}
      {!results.isError && total > rows.length && (
        <p className="mt-1 text-2xs text-ink-faint">
          {t("pagination.range", { from: 1, to: rows.length, total })}
        </p>
      )}
    </div>
  );
}
